import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/modules/common/api/index.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import { CURRENT_ENTITY_QUERY_KEY } from '@/modules/entity/queries/entity.tsx';
import { CURRENT_USER_ACTIVITIES_QUERY_KEY } from '@/modules/investor-dashboard/queries/activity.tsx';
import { CURRENT_ENTITY_SHAREHOLDERS_QUERY_KEY } from '@/modules/investor-dashboard/queries/transactions.tsx';
import { CURRENT_ISSUER_ACTIVITIES_QUERY_KEY } from '@/modules/issuer-dashboard/queries/activities.tsx';
import { CURRENT_ISSUER_PAYMENTS_QUERY_KEY } from '@/modules/issuer-dashboard/queries/payments.tsx';
import {
  CreateShareholderRequest,
  CreateShareholderResponse,
  GetRegisterShareholderListV1SrShareholdersByYourRegisterGetParams,
  GetShareholderByEntityIdResponse,
  GetShareholdersByRegisterResponse,
  UpdateShareholderRequest,
  VerifyEntityPaymentRequest,
} from 'api';

export const CURRENT_REGISTER_SHAREHOLDERS_QUERY_KEY = 'vestra/current-register-shareholders';
export const CURRENT_SHAREHOLDER_COMMUNICATION_QUERY_KEY =
  'vestra/shareholder-communication-setting';

export const useRegisterShareholderList = (
  payload: GetRegisterShareholderListV1SrShareholdersByYourRegisterGetParams,
  options?: { disabled?: boolean; onSuccess?: (data: GetShareholdersByRegisterResponse) => void },
) => {
  const { disabled = false, onSuccess } = options || {};
  return useQuery<GetShareholdersByRegisterResponse>({
    queryKey: [CURRENT_REGISTER_SHAREHOLDERS_QUERY_KEY, JSON.stringify(payload)],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getRegisterShareholderListV1SrShareholdersByYourRegisterGet(
        payload,
      );
      return response.data;
    },
    onSuccess,
    enabled: !disabled,
  });
};

export const useVerifyEntityPaymentInfoMutation = () => {
  return useMutation({
    mutationFn: async (payload: VerifyEntityPaymentRequest & { investorName: string }) => {
      const response = await withErrorHandling(
        api.v1.verifyEntityPaymentInfoV1SrEntityVerifyPaymentInfoPost(payload),
        {
          loading: 'Sending a confirmation email to the investor...',
          success: `A request to verify or update payment details has been sent to ${payload.investorName} by email,
          you will be notified once they are updated or confirmed`,
          notificationDuration: 10000,
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_REGISTER_SHAREHOLDERS_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_ISSUER_ACTIVITIES_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_ISSUER_PAYMENTS_QUERY_KEY]);
    },
  });
};

export const useVerifiedEntityPaymentInfoMutation = () => {
  return useMutation({
    mutationFn: async () => {
      const response = await withErrorHandling(
        api.v1.verifiedEntityPaymentInfoV1SrEntityVerifiedPaymentInfoPost({}),
        {
          loading: 'Loading...',
          success:
            'Your bank information has been verified, your issuer has been notified that they can send payments to this account.',
          notificationDuration: 10000,
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_ENTITY_QUERY_KEY]);
    },
  });
};

export const useShareholderCommunicationSettingQuery = (entityId: number, disabled = false) => {
  return useQuery<GetShareholderByEntityIdResponse>({
    queryKey: [CURRENT_SHAREHOLDER_COMMUNICATION_QUERY_KEY, entityId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getCommunicationSettingsV1SrInvestorCommunicationSettingsGet({
        headers: { ['xEntity-ID']: entityId },
      });
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useShareholderCommunicationSettingUpdateMutation = () => {
  return useMutation({
    mutationFn: async (payload: UpdateShareholderRequest) => {
      const response = await withErrorHandling(
        api.v1.updateCommunicationSettingsV1SrInvestorCommunicationSettingsPatch(payload),
        {
          loading: 'Updating information...',
          success: 'Information updated',
        },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([CURRENT_SHAREHOLDER_COMMUNICATION_QUERY_KEY]);
    },
  });
};

export const useCreateShareholderMutation = ({
  onSuccess,
}: { onSuccess?: (data: CreateShareholderResponse) => void } = {}) => {
  return useMutation({
    mutationFn: async (payload: CreateShareholderRequest) => {
      const response = await withErrorHandling(
        api.v1.createShareholderV1SrShareholdersPost(payload),
        {
          loading: 'Joining of an investor to the register...',
          success: 'Investor added to register',
        },
      );
      return response.data;
    },
    onSuccess: async (data: CreateShareholderResponse) => {
      await queryClient.invalidateQueries([CURRENT_REGISTER_SHAREHOLDERS_QUERY_KEY]);
      await queryClient.invalidateQueries([CURRENT_USER_ACTIVITIES_QUERY_KEY]);
      await queryClient.invalidateQueries([CURRENT_ENTITY_SHAREHOLDERS_QUERY_KEY]);
      onSuccess && onSuccess(data);
    },
  });
};
